import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Modal, Spinner } from 'react-bootstrap'

const phaseAt = (warnAt, expiresAt) => {
  const now = Date.now()
  if (now >= expiresAt) return 'expired'
  if (now >= warnAt) return 'expiring'
  return 'active'
}

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

// Prompts the user to continue their session shortly before it ends, and
// blocks the app once it has.
const SessionExpiryModal = ({ warnAt, expiresAt, onContinue }) => {
  const [phase, setPhase] = useState(() => phaseAt(warnAt, expiresAt))
  const [continuing, setContinuing] = useState(false)

  useEffect(() => {
    const update = () => setPhase(phaseAt(warnAt, expiresAt))
    update()
    const timers = [warnAt, expiresAt].map((at) =>
      setTimeout(update, Math.max(at - Date.now(), 0))
    )
    // Timers stall while a laptop sleeps, so re-check when the tab returns.
    document.addEventListener('visibilitychange', update)

    return () => {
      timers.forEach(clearTimeout)
      document.removeEventListener('visibilitychange', update)
    }
  }, [warnAt, expiresAt])

  const continueSession = async () => {
    setContinuing(true)
    await onContinue()
    setContinuing(false)
  }

  const expired = phase === 'expired'

  return (
    <Modal show={phase !== 'active'} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          {expired ? 'Your session has expired' : 'Your session is about to expire'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {expired
          ? 'Sign in again to keep working. You’ll come back to this page.'
          : `You’ll be signed out at ${formatTime(expiresAt)}. Continue your session to keep working.`}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={continueSession} disabled={continuing}>
          {continuing && <Spinner size="sm" animation="border" className="me-2" />}
          {expired ? 'Sign in again' : 'Continue session'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

SessionExpiryModal.propTypes = {
  warnAt: PropTypes.number.isRequired,
  expiresAt: PropTypes.number.isRequired,
  onContinue: PropTypes.func.isRequired
}

export default SessionExpiryModal
